<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        
        <!-- Add New Invite - Modal Trigger Button -->
        <b-button class="" @click="vendorInviteModal = true" variant="gray-primary">
            <b-icon icon="plus-circle" class="mr-2"></b-icon>
            Single Invite
        </b-button>

        <b-modal
            v-model="vendorInviteModal"
            title="New Invitation"
            size="lg"
            centered
            hide-header
            hide-footer
            no-close-on-backdrop
            body-class="p-0"
            content-class="overflow-auto"
            @hidden="onModalHidden"
            @show="onModalShow"
        >
            <b-card no-body class="border-0">
                <b-overlay :show="savingVendorInvitation" no-wrap>
                    <template #overlay>
                        <div class="text-center">
                            <b-icon icon="envelope-fill" variant="primary" animation="fade" font-scale="3"></b-icon>
                            <h5 class="text-muted mt-4">Sending Invitation</h5>
                        </div>
                    </template>
                </b-overlay>
                <div v-if="loadingUpdate" key="loadingUpdate">
                    <busy-spinner inner-class="my-5" text="Loading..."></busy-spinner>
                </div>
                <div 
                    v-else
                    key="notLoadingUpdate"
                    class="h-100 d-flex flex-column justify-content-between"
                >
                    <b-tabs
                        no-nav-style 
                        justified 
                        v-model="stepsTabIndex" 
                        class="spl-card-nav"
                        content-class="p-4"
                        @input="handleTabChange"
                    >
                        <b-tab 
                            ref="vendorSelectionTab"
                            :disabled="isUpdate"
                        >
                            <template #title>
                                <number-round variant="info" text="1" class="mr-2"></number-round>
                                Select Vendor
                            </template>
                            <div class="row">
                                <div class="col-12">
                                    <div class="border rounded bg-gray-50">
                                        <div role="tab">
                                            <b-button 
                                                block 
                                                v-b-toggle.vendor-saved-accordion 
                                                variant="gray-primary"
                                                class="d-flex justify-content-between no-shadow collapse-controller"
                                            >
                                                <h5 class="mb-0">
                                                    <div>
                                                        <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                                        Add Saved Vendor
                                                    </div>
                                                </h5>
                                                <b-icon icon="chevron-contract" class="collapse-controller-swap when-open"></b-icon>
                                                <b-icon icon="chevron-expand" class="collapse-controller-swap when-closed"></b-icon>
                                            </b-button>
                                        </div>
                                        <b-collapse id="vendor-saved-accordion" visible accordion="vendor-accordion" role="tabpanel">
                                            <b-row class="p-3">
                                                <b-col cols="3">
                                                    <h5>Vendor Search</h5>
                                                    <p class="text-muted">Search for a vendor from your Supplier database</p>
                                                </b-col>
                                                <b-col class="">
                                                    <b-form-group
                                                        id="fieldset-vendor-name"
                                                        label="Vendor Name"
                                                        label-for="input-search-vendor-name"
                                                        label-class="spl-inputs mb-1"
                                                        label-cols="4"
                                                        label-align="right"
                                                    >
                                                        <b-form-input 
                                                            id="input-search-vendor-name" 
                                                            v-model="vendorSearchName" 
                                                            trim
                                                            autofocus
                                                            size="lg"
                                                            autocomplete="off"
                                                            placeholder="Search by Vendor Name"
                                                            @keyup.enter="searchVendors"
                                                        ></b-form-input>
                                                    </b-form-group>
                                                    <div class="d-flex justify-content-center align-items-center">
                                                        <b-button variant="gray-secondary" class="mr-3" :disabled="!vendorSearchName" @click="resetSearchVendors">Reset</b-button>
                                                        <b-button variant="primary" class="px-4" :disabled="!vendorSearchName" @click="searchVendors">Search</b-button>
                                                    </div>
                                                </b-col>
                                                <div class="col-12">
                                                    <div v-if="vendorSearchState !== 'results'" key="noSearchResults" class="h-100" style="min-height: 100px;">
                                                        <transition name="fade-fast" mode="out-in">
                                                            <busy-spinner v-if="vendorSearchState === 'loading'" key="vendorSearchStateLoading"></busy-spinner>
                                                            <div 
                                                                v-else-if="vendorSearchState === 'incomplete'"
                                                                key="vendorSearchStateIncomplete"
                                                                class="py-3 h-100 d-flex justify-content-center align-items-center text-muted"
                                                            >
                                                                <div class="d-inline-block"><h5 class="mb-0 font-weight-light font-size-70">SEARCH RESULTS</h5></div>
                                                            </div>
                                                            <div 
                                                                v-else-if="vendorSearchState === 'empty'"
                                                                key="vendorSearchStateEmpty"
                                                                class="py-3 h-100 d-flex justify-content-center align-items-center text-muted text-center"
                                                            >
                                                                <div>
                                                                    No Results
                                                                    <div class="small">Please try changing your search criteria</div>
                                                                    <div class="small">or <b-link v-b-toggle.vendor-new-accordion>invite a new Vendor</b-link></div>
                                                                </div>
                                                            </div>
                                                        </transition>
                                                    </div>
                                                    <b-collapse 
                                                        key="vendorSearchStateResults"
                                                        :visible="vendorSearchState === 'results'"
                                                    >
                                                        <div class="h-100 d-flex flex-column justify-content-between align-items-center py-3">
                                                            <div class="w-100 max-width-500">
                                                                <!-- <div> Putting heading and table together to keep at top of flexbox -->
                                                                    <div class="w-100 d-flex flex-row justify-content-between align-items-baseline">
                                                                        <h5 class="mb-3">Select a supplier to invite:</h5>
                                                                        <p class="mb-0 text-muted">{{vendorSearchResults.length}} Suppliers Found</p>
                                                                    </div>
                                                                    <b-table
                                                                        ref="vendorSearchResultsTable"
                                                                        :items="vendorSearchResults"
                                                                        :fields="vendorSearchResultsFields"
                                                                        selectable
                                                                        select-mode="single"
                                                                        no-select-on-click
                                                                        small
                                                                        hover
                                                                        :current-page="vendorSearchResultsCurrentPage"
                                                                        :per-page="vendorSearchResultsPerPage"
                                                                        tbody-tr-class="bg-white"
                                                                        class="no-top-border td-align-middle"
                                                                    >  <!-- only allow selection via button? @row-selected="onVendorSelected" -->
                                                                        <template #cell(name)="data">
                                                                            <div class="d-flex align-items-center gap-3 flex-wrap">
                                                                                <div class="">{{ data.value }}</div>
                                                                                <featured-vendor-tag-group
                                                                                    :tags="data.item.company_tags"
                                                                                ></featured-vendor-tag-group>
                                                                            </div>
                                                                        </template>
                                                                        <template #cell(select_button)="data">
                                                                            <!-- only block adding a vendor if they are blocked, not if they don't have any contacts, because we can add them here -->
                                                                            <span v-if="data.item.blocked" key="isBlocked">BLOCKED</span>
                                                                            <b-button v-else-if="checkIsAlreadyInvited(data.item)" key="alreadyInvited" disabled variant="minimal-secondary">Existing</b-button>
                                                                            <b-button v-else key="notInvited" variant="minimal-success" @click="selectSingleVendor(data.item)">Select &rarr;</b-button>
                                                                        </template>
                                                                    </b-table>
                                                                <!-- </div> -->
                                                                <b-pagination
                                                                    v-if="vendorSearchResults.length > vendorSearchResultsPerPage"
                                                                    v-model="vendorSearchResultsCurrentPage"
                                                                    :total-rows="vendorSearchResults.length"
                                                                    :per-page="vendorSearchResultsPerPage"
                                                                    align="right"
                                                                    size="sm"
                                                                    class="mb-0"
                                                                ></b-pagination>
                                                            </div>
                                                        </div>
                                                    </b-collapse>
                                                </div>
                                            </b-row>
                                        </b-collapse>
                                    </div>
                                </div>
                            </div>
                            <div class="row mt-3">
                                <div class="col-12">
                                    <div class="border rounded bg-gray-50">
                                        <div role="tab">
                                            <b-button 
                                                block 
                                                v-b-toggle.vendor-new-accordion 
                                                variant="gray-primary"
                                                class="d-flex justify-content-between no-shadow collapse-controller"
                                            >
                                                <h5 class="mb-0">
                                                    <div>
                                                        <b-icon icon="plus-circle" class="mr-2 collapse-controller-swap"></b-icon>
                                                        Add New Vendor
                                                    </div>
                                                </h5>
                                                <b-icon icon="chevron-contract" class="collapse-controller-swap when-open"></b-icon>
                                                <b-icon icon="chevron-expand" class="collapse-controller-swap when-closed"></b-icon>
                                            </b-button>
                                        </div>
                                        <b-collapse id="vendor-new-accordion" accordion="vendor-accordion" role="tabpanel">
                                            <b-row class="p-3">
                                                <b-col>
                                                    <company-detail-form
                                                        ref="vendorCreateForm"
                                                        @save-complete="handleVendorSaveComplete"
                                                        create-new
                                                        edit-mode
                                                        class="w-100"
                                                    ></company-detail-form>
                                                    <div class="text-right">
                                                        <b-button variant="success" @click="saveNewVendor">Save & Next &rarr;</b-button>
                                                    </div>
                                                </b-col>
                                            </b-row>
                                        </b-collapse>
                                    </div>
                                </div>
                            </div>
                        </b-tab>
                        <b-tab ref="contactSelectionTab" :disabled="!vendorSelectedId" lazy>
                            <template #title>
                                <number-round variant="info" text="2" class="mr-2"></number-round>
                                Select Contacts
                            </template>
                            <div v-if="vendorSelectedId">
                                <div class="d-flex flex-column align-items-center">
                                    <div class="w-100 max-width-700 my-3">
                                        <h5 class="">Select contacts to invite from {{ vendorSelectedName }}:</h5>
                                        <p class="text-muted small mb-2">
                                            All selected contacts at this supplier will be able to view the RFx & contribute to the response from their company.
                                        </p>
                                        <p class="text-muted small mb-2">
                                            Only one response will be allowed from the supplier in total.
                                        </p>
                                    </div>
                                    <div class="w-100 max-width-700">
                                        <div class="bg-gray-100 p-3 rounded-lg mb-2">
                                            <busy-spinner v-if="loadingVendorContacts" key="contactsLoading"></busy-spinner>
                                            <div v-else-if="vendorContacts && vendorContacts.length" key="hasContacts">
                                                <b-form-checkbox 
                                                    v-for="contactObj in vendorContacts"
                                                    :key="contactObj.id"
                                                    :value="contactObj"
                                                    v-model="inviteData.recipients"
                                                    :disabled="!contactObj.can_be_invited_to_rfx"
                                                    class="mb-1"
                                                >
                                                    <div class="d-flex align-items-center gap-4">
                                                        <span class="font-weight-500">{{ contactObj.full_name }}</span>
                                                        <span 
                                                            class="font-size-80"
                                                            :class="contactObj.email ? 'text-muted' : 'text-danger font-weight-600'"
                                                        >
                                                            {{ contactObj.email ? contactObj.email : 'EMAIL ADDRESS REQUIRED' }}
                                                            <a 
                                                                v-if="!contactObj.email" 
                                                                href="#" 
                                                                class="ml-1 text-muted font-size-80"
                                                                @click="startUpdateContact(contactObj.id)"
                                                            >Update Now</a>
                                                        </span>
                                                        <b-badge 
                                                            v-if="getSourcingRole(contactObj)" 
                                                            variant="soft-primary" 
                                                            class="text-uppercase"
                                                        >{{ getSourcingRole(contactObj).name }}</b-badge>
                                                    </div>
                                                </b-form-checkbox>
                                            </div>
                                            <div v-else key="noContacts" class="d-flex align-items-center justify-content-center">
                                                <b-button variant="sharp-success" class="" @click="startAddNewContact">
                                                    <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                                    Add New Contact
                                                </b-button>
                                            </div>
                                        </div>

                                        <div v-if="!loadingVendorContacts && vendorContacts.length" class="d-flex justify-content-between align-items-center pb-3">
                                            <div>
                                                <b-button variant="minimal-primary" size="sm" @click="selectAllContacts">Select All</b-button>
                                                <b-button variant="minimal-primary" size="sm" @click="unselectAllContacts">Unselect All</b-button>
                                            </div>
                                            <div class="font-weight-bold">
                                                {{ inviteData.recipients.length }} selected contact{{ inviteData.recipients.length === 1 ? '' : 's' }}
                                            </div>
                                            <b-button variant="sharp-success" size="sm" @click="startAddNewContact">
                                                <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                                New Contact
                                            </b-button>
                                        </div>
                                        <contact-create-update-modal
                                            ref="vendorContactModal"
                                            :redirect-after-create="false"
                                            hide-save-redirect
                                            :email-required="true"
                                            save-close-html="Save"
                                            :company-data="{id: vendorSelectedId, name: vendorSelectedName}"
                                            :success-message-time="0"
                                            @update-complete="handleContactUpdateComplete"
                                            @create-complete="handleContactCreateComplete"
                                        ></contact-create-update-modal>
                                    </div>
                                </div>
                            </div>
                        </b-tab>
                        <b-tab ref="messageSendTab" :disabled="!vendorSelectedId || !inviteData.recipients.length">
                            <template #title>
                                <number-round variant="info" text="3" class="mr-2"></number-round>
                                Add Message & Send
                            </template>
                            <div class="row">
                                <div class="col-12">
                                    <div class="row justify-content-around my-2">
                                        <div class="col-4">
                                            <h6 class="hot-edit-label">Sending invitation to:</h6>
                                            <h5>{{vendorSelectedName}}</h5>
                                            <hr>
                                            <h6 class="hot-edit-label">Invited Contacts:</h6>
                                            <ul class="list-flush">
                                                <li 
                                                    v-for="contact in inviteData.recipients" 
                                                    :key="contact.id"
                                                    class="mb-2"
                                                >
                                                    {{contact.full_name}}<br>
                                                    <span class="small text-muted">{{contact.email}}</span>
                                                </li>
                                            </ul>
                                        </div>
                                        <div class="col-8">
                                            <b-form-group
                                                id="fieldset-invite-message"
                                                label="Message to Supplier"
                                                label-for="textarea-invite-message"
                                                label-class="h6"
                                                class="mb-1"
                                            >
                                                <template #label>
                                                    <h6>Additional Message:</h6>
                                                    <ul class="small text-muted list-flush">
                                                        <li>Separate invitation emails will be sent to each invited contact.</li>
                                                        <li>Adding an additional message is optional.  If included, it will be included at the top of the event invitation email.</li>
                                                    </ul>
                                                </template>
                                                <b-form-textarea 
                                                    id="textarea-invite-message" 
                                                    ref="inviteMessageTextarea"
                                                    v-model="inviteData.message" 
                                                    trim
                                                    rows="8"
                                                    placeholder="Write a message to this vendor (optional)"
                                                ></b-form-textarea>
                                                <p v-if="isUpdate" class="mb-0">NOTE:  Showing the message from previously-sent invitation.</p>
                                            </b-form-group>
                                            <div class="">
                                                <b-button variant="minimal-primary" size="sm" @click="showInviteTextSnippetModal">
                                                    <b-icon icon="file-text" class="mr-1"></b-icon>
                                                    <span class="special-text">Insert Saved Snippet</span>
                                                </b-button>
                                            </div>
                                            <b-modal
                                                title="Insert a snippet..."
                                                centered
                                                size="lg"
                                                ref="snippetModal"
                                                v-model="inviteTextSnippetModalShowing"
                                                body-class="p-0"
                                                hide-footer
                                            >
                                                <snippet-manager
                                                    areaSlug="rfx_invite_email"
                                                    mode="insert"
                                                    @insert="handleInviteTextSnippetInsert"
                                                ></snippet-manager>
                                            </b-modal>
                                        </div>
                                    </div>
                                    
                                    <div v-if="savingVendorInvitationErrors.length" class="row text-danger">
                                        <div class="col">
                                            <h6>Please correct the following errors:</h6>
                                            <ul class="list-flush">
                                                <li v-for="error in savingVendorInvitationErrors" :key="error">
                                                    {{error}}
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </b-tab>
                    </b-tabs>
                    <div class="border-top px-3 py-2">
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <b-button v-if="stepsTabIndex > 0" key="previous" variant="minimal-primary" size="lg" @click="stepsTabIndex--">&larr; Previous</b-button>
                                <div v-else key="noprevious">&nbsp;</div>
                            </div>
                            <div>
                                <div v-if="!canSendInvites" class="px-4">
                                    Invitations will be queued and sent when the Event is OPEN.
                                </div>
                            </div>
                            <div>
                                <b-button variant="gray-secondary" size="lg" @click="vendorInviteModal = false" class="mr-3">Cancel</b-button>
                                <b-button v-if="stepsTabIndex === 0" :disabled="!vendorSelectedId" variant="success" size="lg" @click="onVendorSelectionNext">Next &rarr;</b-button>
                                <b-button v-if="stepsTabIndex === 1" :disabled="!inviteData.recipients.length" variant="success" size="lg" @click="stepsTabIndex++">Next &rarr;</b-button>
                                <b-button v-if="stepsTabIndex === 2" :disabled="!inviteData.recipients.length" variant="success" size="lg" @click="postVendorInvite">{{ isUpdate ? 'Update' : 'Send'}} Invitation</b-button>
                            </div>
                        </div>
                    </div>

                </div> 

            </b-card>
        </b-modal>

    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import NumberRound from './functional-comps/NumberRound';
import FeaturedVendorTagGroup from './vendors/components/FeaturedVendorTagGroup.vue';
import SnippetManager from './snippets/SnippetManager.vue';
import { SOURCING_TENANT_ROLE_SLUG, findTenantRole, hasTenantRole } from '../js/utils/tenantRoleUtils';

export default {
    name: 'RfxVendorInviteModal',
    components: {
        NumberRound,
        FeaturedVendorTagGroup,
        SnippetManager,
    },
    props: {
        rfxPk: {
            type: [Number, String],
            required: true,
        },
        inviteEmailText: {
            type: String,
            required: false,
        },
        invitedVendorIds: {  // array of vendor IDs who are already invited
            type: Array,
            required: false,
        },
        canSendInvites: {
            // can invites be SENT - used to show message if things will be queued
            type: Boolean,
            default: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            vendorInviteModal: false,  // v-model for showing/hiding the main modal
            stepsTabIndex: 0,  // main index keeper for wizard steps
            // update related things
            isUpdate: false,  // if this is UPDATE to existing invite, toggle this true
            loadingUpdate: false,  // loading flag for when fetching update data
            existingInviteId: '',  // if we're working with isUpdate, stash the ID here
            existingInviteContactIds: [],  // array of TU IDs that were selected on EXISTING invite (if isUpdate)
            // vendor search stuff
            vendorSearchName: '',  // from the vendor search by name box
            loadingVendorSearch: false,
            vendorSearchResults: [],  // always keep this an Array, empty array if no search yet, or no results
            vendorSearchComplete: false,  // set to true if we did search... reset to false when resetting inputs
            vendorSearchResultsFields: [
                {
                    key: 'name',
                    label: 'Supplier Name',
                },
                {
                    key: 'select_button',
                    label: '',
                    class: 'text-right',
                }
            ],
            vendorSearchResultsCurrentPage: 1,
            vendorSearchResultsPerPage: 10,
            vendorSelectedArray: [],  // array that holds our selected vendor... should only have one item..., we grab [0] when posting the invite. its an array because selection from a b-table is always an array
            // vendor contacts stuff
            loadingVendorContacts: false,
            vendorContacts: [],  // array of contacts/TU from API, need at least id, full_name, email
            inviteData: {  // main object to hold recipients and message for sending to API
                recipients: [],  // existing contacts -- full objects.  we'll map to IDs later.
                message: '',  // invite message  
                // vendor object for inviteData comes from vendorSelectedArray (array... so grab [0])
            },
            savingVendorInvitation: false,
            savingVendorInvitationErrors: [],  // if we add duplicate, etc. we'll put errors here -- will be an object
            // SNIPPETS
            inviteTextSnippetPosition: 0,  // index/position in the textarea where we insert... we capture it when user clicks to insert snippet
            inviteTextSnippetModalShowing: false,  // v-model with the modal
        }
    },
    computed: {
        vendorSelected: function() {  // returns OBJECT of the selected vendor
            if (this.vendorSelectedArray.length < 1 || isEmpty(this.vendorSelectedArray[0])) return {};
            return this.vendorSelectedArray[0];
        },
        vendorSelectedId: function() {
            if (isEmpty(this.vendorSelected)) return null;
            return this.vendorSelected.id;
        },
        vendorSelectedName: function() {
            if (isEmpty(this.vendorSelected)) return '';
            return this.vendorSelected.name;
        },
        vendorSearchState: function() {
            // returns 'loading' or 'empty' or 'results' or 'incomplete' based on status of search
            if (this.loadingVendorSearch) return 'loading';
            if (this.vendorSearchComplete && this.vendorSearchResults.length) return 'results';
            if (this.vendorSearchComplete && !this.vendorSearchResults.length) return 'empty';
            return 'incomplete';  // return 'notyet' if we get here because search will not be done yet
        },
    },
    methods: {
        getSourcingRole(tuObj) {
            // the built-in sourcing TenantRole if this TU holds it (show its name), otherwise undefined
            return findTenantRole(tuObj, SOURCING_TENANT_ROLE_SLUG);
        },
        // PUBLIC METHODS 
        updateExisting: function(existingData) {
            // call this from parent to update an EXISTING INVITE / RfxResponse to modify contacts, etc 
            console.log(existingData);
            this.isUpdate = true;
            this.loadingUpdate = true;
            this.stepsTabIndex = 1;  // go to 'contacts' tab directly
            this.vendorInviteModal = true; // show the modal ASAP to show some action, but set loadingUpdate true
            this.existingInviteId = existingData.id;
            let vendorObj = {  // reconstruct the vendor object, only need id and name
                id: existingData.vendor,
                name: existingData.vendor_name,
            };
            this.vendorSelectedArray = [vendorObj];  // set the selected vendor 
            let alreadyInvitedTusIds = existingData.invited_tus.map(tu => tu.id);  // invited_tus will be array of objects, map to array of IDs
            this.existingInviteContactIds = [...alreadyInvitedTusIds];  // set on local data
            this.getVendorContacts(vendorObj.id)
                .then(() => {
                    this.inviteData.message = existingData.invitation_note;
                    this.selectExistingContacts();  // select the contacts who were previously chosen
                    // then finally run off loadingUpdate to show things
                    this.loadingUpdate = false;  // turn off loading flag to show things
                });
        },
        // non-public methods below:
        handleError: function(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        clearError: function() {
            this.errorMsg = 'Error';
            this.error = false;
        },
        searchVendors: function() {
            if (!this.vendorSearchName) return;
            this.loadingVendorSearch = true;
            this.resetVendorInviteDetails();  // reset things that depend on selecting a vendor
            // OLD - //suppliosApi.internal.vendorComplexSearch(this.vendorSearchName)
            // AUG 23 2023 - changing this to use endpoint on the RFX so we can filter out any using RFX / SourcingSettings logic
            const axiosConfig = {
                params: {
                    name: this.vendorSearchName,
                }
            };
            // bulk invite vendor list returns { id, name, disabled, blocked, disabled_reason, tus, company_tags }
            suppliosApi.rfx.get_possible_bulk_invite_vendors(this.rfxPk, axiosConfig)
            .then(response => {
                this.clearError();
                console.log('got vendor list from search, full response below');
                console.log(response);
                this.vendorSearchResults = response.data;
                this.vendorSearchComplete = true;
            })
            .catch(error => this.handleError(error, 'Error while searching for vendors'))
            .finally(() => this.loadingVendorSearch = false)
        },
        onVendorSelected: function(selection) {
            // selection will be an array, even when in single-select mode
            // if you have one selected, but its already invited and its NOT an update, return empty object
            if (!this.isUpdate && selection && selection.length && this.checkIsAlreadyInvited(selection[0])) {
                this.vendorSelectedArray = [];
                return 
            }
            this.vendorSelectedArray = [...selection];
        },
        selectSingleVendor: function(vendorObject) {
            this.vendorSelectedArray = [vendorObject];
            this.$nextTick(() => this.onVendorSelectionNext());
            //this.onVendorSelectionNext();
        },
        onVendorSelectionNext: function() {
            this.stepsTabIndex++;
            this.getVendorContacts();
        },
        resetVendorInviteDetails: function() {
            // reset selected vendor, contact array, contact selections, message, etc... 
            // call this when you do a new vendor search (this only doesn't reset things related to the search that was just done)
            this.vendorSelectedArray = [];
            this.vendorContacts = [];
            this.inviteData.recipients = [];
            this.inviteData.message = '';  // setMessage() will override this, if saved message is set, but still need to reset it here
            this.savingVendorInvitationErrors = [];
            this.vendorSearchResultsCurrentPage = 1;
            this.setMessage();
        },
        resetSearchVendors: function() {
            // from reset button under vendor search boxes -- reset inputs and vendor list
            this.vendorSearchName = '';
            this.vendorSearchResults = [];
            this.vendorSearchComplete = false;
            this.resetVendorInviteDetails();
        },
        getVendorContacts: function(vendorId) {
            // you can either pass in vendorId directly, or get from this.vendorSelectedId -- must do one
            let finalVendorId = vendorId ? vendorId : this.vendorSelectedId;
            if (!finalVendorId) return;  // return if you didn't get a vendorId
            this.loadingVendorContacts = true;
            return suppliosApi.vendorStandard.listContacts(finalVendorId)
                .then(response => {
                    this.clearError();
                    console.log('got list of contacts for this vendor, full response below');
                    console.log(response);
                    this.vendorContacts = response.data;
                    // preselect the contacts who hold the sourcing TenantRole (Oct 2026, was is_def_sourcing) -- make sure to set full objects
                    this.inviteData.recipients = this.vendorContacts.filter(vc => hasTenantRole(vc, SOURCING_TENANT_ROLE_SLUG));
                    return true;
                })
                .catch(error => {
                    this.handleError(error, 'Error while getting contacts');
                    return false;
                })
                .then(wasSuccess => {
                    this.loadingVendorContacts = false;
                    return wasSuccess;
                })
        },
        onContactSelected: function(selectionArray) {
            this.inviteData.recipients = [...selectionArray];
        },
        selectExistingContacts: function() {
            // non-table version of below
            if (this.existingInviteContactIds && Array.isArray(this.existingInviteContactIds) && !!this.existingInviteContactIds.length) {
                const newSelectedObjects = this.vendorContacts.filter(vc => this.existingInviteContactIds.includes(vc.id));
                this.inviteData.recipients = newSelectedObjects;
            }
        },
        selectExistingContactRows: function() {
            // call this when showing the contact table to preselect the table rows for EXISTING invited_tus if its an update
            // if you have any preselected contacts, their IDs are in this.existingInviteContactIds (array)
            console.log('From selectExistingContactRows, dumping existingInviteContactIds, then vendorContacts');
            console.log(this.existingInviteContactIds);
            console.log(this.vendorContacts);
            if (this.existingInviteContactIds && Array.isArray(this.existingInviteContactIds) && !!this.existingInviteContactIds.length) {
                //const newSelectedObjects = this.vendorContacts.filter(vc => this.existingInviteContactIds.includes(vc.id));
                //this.inviteData.recipients = newSelectedObjects;
                // BELOW WAS OLD TABLE VERSION
                /* this.existingInviteContactIds.forEach(contactId => {
                    // for each existing invited_tu, look through rows of contact table, find index, call selectRow(index) on it
                    let foundIndex = this.vendorContacts.findIndex(cont => cont.id === contactId);  // contactId is from preselected list
                    if (foundIndex >= 0) {  // findIndex returns -1 if not found
                        this.$refs.vendorContactsTable.selectRow(foundIndex);
                    }
                }); */
            }
        },
        // NEW VERSION OF SELECT/UNSELECT ALL USING CHECKBOXES
        selectAllContacts() {
            // select all that CAN be selected
            const contactsToInvite = this.vendorContacts.filter(contactObj => contactObj.can_be_invited_to_rfx);
            this.inviteData.recipients = cloneDeep(contactsToInvite);  // array of contacts
        },
        unselectAllContacts() {
            // empty the list
            this.inviteData.recipients = [];
        },
        // OLD VERSIONS LEFT FROM TABLE OF CONTACTS
        selectAllRows: function(refName) {
            // calls .selectAllRows() on the ref you pass in -- ref must be on a b-table
            this.$refs[refName].selectAllRows();
        },
        unselectAllRows: function(refName) {
            // calls .clearSelected() on the ref you pass in -- ref must be on a b-table
            this.$refs[refName].clearSelected();
        },
        formatInviteData: function() {
            // this formats data for how you want it into API
            // output: see example object in data
            // source will be which button we're calling from, so we know where to 'grab' data
            // update AUG24 - only using 'existing' version of this, new vendor and new TUs get created before this 
            let outputObj = {};  // use cloneDeep
            outputObj['invitation_note'] = this.inviteData.message;
            outputObj['invited_tus'] = this.inviteData.recipients.map(rec => rec.id);  // pull out recipient ids (TU ids)
            outputObj['vendor'] = this.vendorSelectedArray[0].id;
            return outputObj;
        },
        postVendorInvite: function() {
            // this just directs to create or update...
            if (this.isUpdate) {
                this.updateVendorInvite();
            } else {
                this.createVendorInvite();
            }
        },
        createVendorInvite: function() {
            // handle the API post to create new invitation from saved vendor DB
            this.savingVendorInvitation = true;
            let newInviteObj = this.formatInviteData();  // this is object for invite from saved vendor list
            console.log(newInviteObj);
            suppliosApi.rfx.createInvitedResponse(this.rfxPk, newInviteObj)
            .then(response => {
                this.clearError();
                console.log('Posted newInviteData, response below');
                console.log(response);
                this.handleInviteSuccess(response.data);
            })
            .catch(error => {
                let errorListObj = error.response.data;
                this.savingVendorInvitationErrors = Object.values(errorListObj).flat();
                /*
                if (error.response.status === 409) {
                    // we use 409 for duplicates
                    let errorListObj = error.response.data;
                    this.savingVendorInvitationErrors = Object.values(errorListObj).flat();
                } else {
                    this.handleError(error, 'Error while saving new Invite');
                }*/
            })
            .finally(() => this.savingVendorInvitation = false)
        },
        updateVendorInvite: function() {
            // only for handling UPDATES to existing invites -- PUT request to RfxResponse endpoint
            if (!this.isUpdate || !this.existingInviteId) return Promise.resolve(false);  // check to make sure you have things for being an update
            this.savingVendorInvitation = true;
            let inviteData = this.formatInviteData();
            return suppliosApi.rfxResponse.updateInvitedResponse(this.existingInviteId, inviteData)
                .then(response => {
                    // it just responds with result: 'ok' most likely
                    this.clearError();
                    this.handleInviteSuccess(response.data);
                    return true;
                })
                .catch(error => {
                    let errorListObj = error.response.data;
                    this.savingVendorInvitationErrors = Object.values(errorListObj).flat();
                    return false;
                })
                .then(success => {
                    this.savingVendorInvitation = false;
                    return success;
                });
        },
        handleInviteSuccess: function(inviteObj) {
            // call this after we successfully post new invite
            // hide modal, show success thing, emit event to parent
            // no need to reset inputs (onModalHidden will do that)
            console.log('from handleInviteSuccess', inviteObj);
            this.$emit('new-invite');
            this.vendorInviteModal = false;  // hide the modal
            let inviteCreatedMsg = 'Invitation has been sent';
            let smStateValue = inviteObj.sm_state;
            if (smStateValue === 'queued') {
                inviteCreatedMsg = 'Invitation has been queued';
            }
            let successMsg = this.isUpdate ? 'Invitation has been updated' : inviteCreatedMsg;
            this.$toast.success(successMsg);
        },
        resetIsUpdate: function() {
            // reset things related to this being an update
            this.isUpdate = false;
            this.loadingUpdate = false;
            this.existingInviteId = '';
            this.existingInviteContactIds = [];
        },
        onModalHidden: function() {
            this.resetSearchVendors();
            this.resetVendorInviteDetails();
            this.resetIsUpdate();  // reset things related to it being an update
            this.stepsTabIndex = 0;
            //this.resetNewVendorData();
            //this.newVendorTabIndex = 0;
        },
        onModalShow: function() {
            // set the boilerplate invite email text
            this.setMessage();
        },
        setMessage: function() {
            if (this.inviteEmailText) {
                this.inviteData.message = this.inviteEmailText;
            }
        },
        // new methods for new combined layout, Aug 2020
        saveNewVendor: function() {
            // call method on embedded company-detail-form
            this.$refs.vendorCreateForm.saveNewItem();
            // when save is done, component will emit 'save-complete', catch it there and do something
        },
        handleVendorSaveComplete: function(newVendorObj) {
            // function to handle save-complete event from new vendor form:  company-detail-form
            console.log(newVendorObj);
            this.selectSingleVendor(newVendorObj);  // use same method as when selecting single vendor from list using button (DRY)
            // that handles going to next tab too
        },
        startAddNewContact: function() {
            // shows modal for adding new contact to the selected company
            this.$refs.vendorContactModal.startCreate();
        },
        handleContactCreateComplete: function(newContactObj) {
            // function to handle create-complete event from new contact form 
            // now record which contacts were already selected (if any) and the id of new contact, then refresh contact list, then select those contacts again, programmatically
            console.log(newContactObj);
            let newContactId = newContactObj.id;
            let previousSelectedContactIds = this.inviteData.recipients.map(cont => cont.id);  // array of contact IDs
            previousSelectedContactIds.push(newContactId);
            let getContactsPromise = this.getVendorContacts();  // returns promise that resolves to boolean
            // this.getVendorContacts() sets the contact list on this.vendorContacts
            getContactsPromise.then(() => {
                // go through the new full list of vendorContacts, pull out the ones you had selected (and new one) and set those on selected vendors
                const newSelectedObjects = this.vendorContacts.filter(vc => previousSelectedContactIds.includes(vc.id));
                this.inviteData.recipients = newSelectedObjects;
            });
            // BELOW WAS FOR TABLE VERSION
            /* this.$nextTick(() => {
                return getContactsPromise.then(() => {
                    let newSelectedIndexes = previousSelectedContactIds.map(contactId => {
                        console.log(JSON.stringify(this.vendorContacts));
                        return this.vendorContacts.findIndex(contact => contact.id === contactId);  // look through NEW list of contacts for the matching ID and return its index in list
                    });  // gives us 1D array of indexes we should select, nos select them in the table
                    console.log('selected indexes are: ' + JSON.stringify(newSelectedIndexes));
                    newSelectedIndexes.forEach(selected => {
                        console.log('this just before ref to table - this is: ');
                        console.log(this);
                        this.$refs.vendorContactsTable.selectRow(selected);
                    });                
                    return newSelectedIndexes;  // return this from .then() in promise chain, just to return something
                });
            }); */
        },
        startUpdateContact(contactId) {
            // called when we don't have an email address or something, and need to update it
            // show update modal, then trigger something when modal is closed
            this.$refs.vendorContactModal.startUpdate(contactId);
        },
        handleContactUpdateComplete() {
            // payload (arg) here will be updatedContactObj -- if you need it
            // just reload the list of contacts
            // any existing selections should stay selected... ?
            this.getVendorContacts();
        },
        handleTabChange: function(newTabIndex) {
            // called whenever tabs are changed, passes in newTabIndex (zero based) - from @input on b-tabs
            if (newTabIndex === 1) {
                // if changing to contacts tab, call method to preselect rows if any were set
                //this.selectExistingContactRows();
            }
        },
        checkIsAlreadyInvited: function(vendorObj) {
            // return Boolean - true if vendor is already invited, false if not already invited
            if (!this.invitedVendorIds) return false;
            return this.invitedVendorIds.includes(vendorObj.id);
        },
        // SNIPPETS
        getInviteEmailTextarea() {
            // get the BS-Vue textarea component for editing the email text - used in a couple places, just a helper for that
            // returns undefined if can't find it (maybe not in the right mode)
            // returns the b-form-textarea component (or undefined)
            return this.$refs['inviteMessageTextarea'];
        },
        showInviteTextSnippetModal() {
            // we will also record the current cursor position in the textarea so we know where to insert
            // we do this before it loses focus
            // have to traverse refs for now... to our HotEditTextarea, then to its ref on the BFormTextarea, then get .selectionEnd property from that
            const textarea = this.getInviteEmailTextarea();
            this.inviteTextSnippetPosition = textarea ? textarea.selectionEnd : 0;
            this.inviteTextSnippetModalShowing = true;
        },
        handleInviteTextSnippetInsert(content) {
            // get the textarea, insert content at our stashed position
            // we use setRangeText(replacement, start) - that is native JS way to insert text (focus it first)
            // but the BS-Vue textarea also implements it, so call it on the BS-Vue textarea component
            console.log('insert this content at position', this.inviteTextSnippetPosition, content);
            const textarea = this.getInviteEmailTextarea();
            if (!textarea) {
                console.error('Cannot find textarea to insert the snippet!');
                return;
            }
            textarea.focus();
            textarea.setRangeText(content);  // the bs-vue implementation doesn't seem to support args of (content, startPosition)
            // just setting the text does not trigger COMPONENT to update... so we need to do it manually...
            const newValue = textarea.$el.value;  // that will have the new text
            this.inviteData.message = newValue;  // that will update vue component, and update the textarea
            // done, now close modal
            this.inviteTextSnippetModalShowing = false;
        },

    }
}
</script>

<style>

</style>